import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * ReactArtifactRenderer — renders AI-generated React components in a sandboxed iframe.
 *
 * Security: The iframe uses sandbox="allow-scripts" WITHOUT allow-same-origin,
 * which means the rendered code CANNOT access the parent page's DOM, cookies,
 * localStorage, or any same-origin APIs. This prevents XSS from AI-generated code.
 */
const ReactArtifactRenderer = ({ code }) => {
  const { t } = useTranslation('common');
  const [error, setError] = useState(null);
  const iframeRef = useRef(null);

  useEffect(() => {
    if (!code) return;
    setError(null);

    try {
      // Messages shown by the iframe itself (app chrome, not the generated component)
      const iframeMessages = JSON.stringify({
        error: t('ui.error'),
        noComponent: t('reactArtifact.noComponent'),
        compilationError: t('reactArtifact.compilationError'),
        compileFailed: t('reactArtifact.compileFailed'),
      }).replace(/</g, '\\u003c');

      // Build a self-contained HTML document that the sandboxed iframe will render.
      // We include React, ReactDOM, Babel, and Recharts via CDN so the iframe
      // has everything it needs without accessing the parent origin.
      const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <style>
    body { margin: 0; padding: 16px; font-family: system-ui, -apple-system, sans-serif; }
    .error { color: #d32f2f; padding: 16px; background: #ffebee; border-radius: 4px; border: 1px solid #ffcdd2; }
    .error pre { margin-top: 8px; font-size: 12px; white-space: pre-wrap; word-break: break-word; }
  </style>
  <script src="https://unpkg.com/react@18/umd/react.production.min.js" crossorigin></script>
  <script src="https://unpkg.com/react-dom@18/umd/react-dom.production.min.js" crossorigin></script>
  <script src="https://unpkg.com/@babel/standalone/babel.min.js"></script>
  <script src="https://unpkg.com/recharts@2/umd/Recharts.js"></script>
</head>
<body>
  <div id="root"></div>
  <script>
    var MSG = ${iframeMessages};
    try {
      var code = ${JSON.stringify(code)};

      // Remove import/export statements
      var processedCode = code
        .replace(/import\\s+.*?from\\s+['"].*?['"];?\\s*/g, '')
        .replace(/import\\s+['"].*?['"];?\\s*/g, '')
        .replace(/export\\s+default\\s+/g, 'var ExportedComponent = ')
        .replace(/ReactDOM\\.render\\s*\\([\\s\\S]*?\\);?\\s*/g, '')
        .trim();

      // Transform JSX
      var transformed = Babel.transform(processedCode, {
        presets: ['react', 'env'],
        plugins: []
      }).code.replace('"use strict";', '').trim();

      // Execute in a function scope with React globals
      var fn = new Function(
        'React', 'ReactDOM', 'useState', 'useEffect', 'useRef',
        'useCallback', 'useMemo', 'Recharts',
        'LineChart', 'Line', 'BarChart', 'Bar', 'PieChart', 'Pie',
        'AreaChart', 'Area', 'RadarChart', 'Radar', 'ScatterChart', 'Scatter',
        'ComposedChart', 'ResponsiveContainer', 'XAxis', 'YAxis',
        'CartesianGrid', 'Tooltip', 'Legend', 'Cell', 'LabelList',
        'ReferenceLine', 'ReferenceArea', 'ReferenceDot', 'Brush', 'Dot',
        transformed + '\\n' +
        'if (typeof ExportedComponent !== "undefined") return ExportedComponent;' +
        'if (typeof Counter !== "undefined") return Counter;' +
        'if (typeof Component !== "undefined") return Component;' +
        'if (typeof App !== "undefined") return App;' +
        'return null;'
      );

      var GeneratedComponent = fn(
        React, ReactDOM,
        React.useState, React.useEffect, React.useRef,
        React.useCallback, React.useMemo,
        Recharts,
        Recharts.LineChart, Recharts.Line,
        Recharts.BarChart, Recharts.Bar,
        Recharts.PieChart, Recharts.Pie,
        Recharts.AreaChart, Recharts.Area,
        Recharts.RadarChart, Recharts.Radar,
        Recharts.ScatterChart, Recharts.Scatter,
        Recharts.ComposedChart,
        Recharts.ResponsiveContainer,
        Recharts.XAxis, Recharts.YAxis,
        Recharts.CartesianGrid,
        Recharts.Tooltip,
        Recharts.Legend,
        Recharts.Cell,
        Recharts.LabelList,
        Recharts.ReferenceLine,
        Recharts.ReferenceArea,
        Recharts.ReferenceDot,
        Recharts.Brush,
        Recharts.Dot
      );

      if (GeneratedComponent) {
        var root = ReactDOM.createRoot(document.getElementById('root'));
        root.render(React.createElement(GeneratedComponent));
      } else {
        document.getElementById('root').innerHTML =
          '<div class="error"><strong>' + MSG.error + ':</strong> ' + MSG.noComponent + '</div>';
      }
    } catch (err) {
      document.getElementById('root').innerHTML =
        '<div class="error"><strong>' + MSG.compilationError + ':</strong><pre>' +
        (err.message || MSG.compileFailed).replace(/</g, '&lt;') +
        '</pre></div>';
    }
  </script>
</body>
</html>`;

      // Use srcdoc to load content into the sandboxed iframe
      if (iframeRef.current) {
        iframeRef.current.srcdoc = htmlContent;
      }
    } catch (err) {
      setError(err.message || t('reactArtifact.prepareFailed'));
    }
  }, [code, t]);

  if (error) {
    return (
      <div style={{
        color: '#d32f2f',
        padding: '16px',
        backgroundColor: '#ffebee',
        borderRadius: '4px',
        border: '1px solid #ffcdd2'
      }}>
        <strong>{t('ui.error')}:</strong>
        <pre style={{
          marginTop: '8px',
          fontSize: '12px',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word'
        }}>
          {error}
        </pre>
      </div>
    );
  }

  return (
    <iframe
      ref={iframeRef}
      sandbox="allow-scripts"
      title={t('reactArtifact.iframeTitle')}
      style={{
        width: '100%',
        minHeight: '400px',
        border: '1px solid #e0e0e0',
        borderRadius: '4px',
        backgroundColor: '#fff',
      }}
    />
  );
};

export default ReactArtifactRenderer;
